import { Location, CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { IonicModule } from '@ionic/angular';

import { ApiService } from 'src/services/api.service';

@Component({
  standalone: true,
  imports: [CommonModule, IonicModule],
  selector: 'app-conversation-file-viewer',
  template: `
    <ion-header>
      <ion-toolbar>
        <ion-buttons slot="start">
          <ion-button (click)="goBack()">
            <ion-icon name="arrow-back"></ion-icon>
          </ion-button>
        </ion-buttons>
        <ion-title>{{ fileName || 'File Viewer' }}</ion-title>
        <ion-buttons slot="end">
          <ion-button (click)="downloadFile()">
            <ion-icon name="download-outline"></ion-icon>
          </ion-button>
        </ion-buttons>
      </ion-toolbar>
    </ion-header>

    <ion-content class="ion-padding">
      <div *ngIf="isLoading" class="loading-container">
        <ion-spinner></ion-spinner>
        <p>Loading file...</p>
      </div>

      <div *ngIf="error" class="error-container">
        <ion-icon name="alert-circle-outline" color="danger"></ion-icon>
        <p>{{ error }}</p>
      </div>

      <div *ngIf="fileUrl && !isLoading && !error" class="file-container">
        <img *ngIf="isImage" [src]="fileUrl" [alt]="fileName" />
        <div *ngIf="!isImage" class="non-image-file">
          <ion-icon name="document-outline" size="large"></ion-icon>
          <p>{{ fileName }}</p>
          <ion-button (click)="downloadFile()" expand="block">
            <ion-icon name="download-outline" slot="start"></ion-icon>
            Download File
          </ion-button>
        </div>
      </div>
    </ion-content>
  `,
  styles: [
    `
      .loading-container,
      .error-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        height: 100%;
        text-align: center;
      }

      .error-container ion-icon {
        font-size: 64px;
        margin-bottom: 16px;
      }

      .file-container {
        display: flex;
        justify-content: center;
        align-items: center;
        height: 100%;
      }

      .file-container img {
        max-width: 100%;
        max-height: 100%;
        object-fit: contain;
      }

      .non-image-file {
        text-align: center;
      }

      .non-image-file ion-icon {
        font-size: 128px;
        margin-bottom: 24px;
      }

      .non-image-file p {
        font-size: 18px;
        margin-bottom: 24px;
      }
    `,
  ],
})
export class ConversationFileViewerComponent implements OnInit {
  conversationId: string;
  fileId: string;
  fileName: string;
  fileUrl: string;
  isImage = false;
  isLoading = true;
  error: string;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private apiService: ApiService,
    private location: Location,
  ) {}

  async ngOnInit() {
    // Try to get conversationId from current route first (when loaded directly),
    // fall back to parent route (when loaded as child of conversation-details)
    this.conversationId =
      this.route.snapshot.params['conversationId'] || this.route.parent?.snapshot.params['conversationId'];
    this.fileId = this.route.snapshot.params['fileId'];

    if (!this.conversationId || !this.fileId) {
      this.error = 'Invalid file URL';
      this.isLoading = false;
      return;
    }

    await this.loadFile();
  }

  async loadFile() {
    try {
      this.isLoading = true;
      this.error = null;

      const response: any = await this.apiService.download(
        `/conversations/${this.conversationId}/files/${this.fileId}/full`,
        'GET',
      );

      const contentDisposition: string = response.headers.get('content-disposition');
      const contentType = response.headers.get('content-type');

      this.fileName =
        contentDisposition
          ?.split(';')
          .find((p) => p.startsWith('filename'))
          ?.split('=')[1] ?? 'file';

      this.isImage = contentType?.startsWith('image/') ?? false;

      const reader = new FileReader();
      reader.readAsDataURL(response.body);

      reader.onloadend = () => {
        this.fileUrl = reader.result.toString();
        this.isLoading = false;
      };
    } catch (e) {
      console.error('Failed to load file:', e);
      this.error = 'Failed to load file. You may not have permission to view this file.';
      this.isLoading = false;
    }
  }

  goBack() {
    // Extract the inbox path from current URL to navigate to conversation details
    // e.g., /dashboard/inbox/all/conversations/123/files/456 -> /dashboard/inbox/all/conversations/123
    const url = this.router.url;
    const conversationUrl = url.replace(/\/files\/[^/]+$/, '');

    if (conversationUrl && conversationUrl !== url) {
      this.router.navigateByUrl(conversationUrl);
    } else {
      this.location.back();
    }
  }

  async downloadFile() {
    if (!this.fileUrl) {
      return;
    }

    const link = document.createElement('a');
    link.href = this.fileUrl;
    link.download = this.fileName;
    link.click();
  }
}
